import { create } from 'zustand';
import skillService, { SkillFlat } from '../services/skill.service';

interface SkillStore {
  // Cache: id -> SkillFlat
  skills: Record<number, SkillFlat>;
  // Track which containers have had their roots loaded
  loadedContainers: Set<number | string>;
  // Track which skill IDs have had their children fetched
  loadedChildren: Set<number>;
  // Loading state
  loading: boolean;
  // Cache version counter - bumped after each BFS batch so UI re-renders
  cacheVersion: number;

  /** Load root skills (no parent) for a container. Cached. */
  loadRoots: (containerId: number) => Promise<SkillFlat[]>;

  /** Load children of given skill IDs. Fetches only IDs not already in cache. */
  loadChildren: (parentIds: number[]) => Promise<SkillFlat[]>;

  /** Load children of a single skill. Returns the children from cache. */
  expandSkill: (skillId: number) => Promise<SkillFlat[]>;

  /** BFS-load all descendants for given containers. Bumps cacheVersion after each level. */
  loadAllDescendants: (containerIds: number[]) => Promise<void>;

  /** Get a skill from cache */
  getSkill: (id: number) => SkillFlat | undefined;

  /** Get children of a skill from cache (both parent FK children and SkillLink refs) */
  getChildren: (skillId: number) => Array<SkillFlat & { is_reference: boolean; skill_link_id: number | null }>;

  /** Invalidate cache for a container (after create/update/delete) */
  invalidateContainer: (containerId: number) => void;

  /** Invalidate a single skill and its parent */
  invalidateSkill: (skillId: number) => void;

  /** Remove a pattern from all skills' reference_patterns_details (after pattern deletion) */
  removePattern: (patternId: number) => void;

  /** Clear all cache */
  clearCache: () => void;
}

const useSkillStore = create<SkillStore>((set, get) => ({
  skills: {},
  loadedContainers: new Set(),
  loadedChildren: new Set(),
  loading: false,
  cacheVersion: 0,

  loadRoots: async (containerId: number) => {
    const state = get();
    // Return cached if already loaded
    if (state.loadedContainers.has(containerId)) {
      return Object.values(state.skills).filter(
        s => s.container === containerId && s.parent === null
      );
    }

    set({ loading: true });
    try {
      const roots = await skillService.getFlat({ container: containerId });
      set(prev => {
        const newSkills = { ...prev.skills };
        for (const s of roots) {
          newSkills[s.id] = s;
        }
        const newLoaded = new Set(prev.loadedContainers);
        newLoaded.add(containerId);
        return { skills: newSkills, loadedContainers: newLoaded, loading: false };
      });
      return roots;
    } catch (e) {
      set({ loading: false });
      throw e;
    }
  },

  loadChildren: async (parentIds: number[]) => {
    const state = get();

    // Collect all child IDs we need to fetch
    const neededIds = new Set<number>();
    for (const pid of parentIds) {
      const parent = state.skills[pid];
      if (!parent) continue;
      // FK children
      for (const cid of parent.children_ids) {
        if (!state.skills[cid]) neededIds.add(cid);
      }
      // SkillLink children
      for (const link of parent.child_link_ids) {
        if (!state.skills[link.skill_id]) neededIds.add(link.skill_id);
      }
    }

    if (neededIds.size === 0) {
      // Mark as loaded even if nothing to fetch
      set(prev => {
        const newLoaded = new Set(prev.loadedChildren);
        for (const pid of parentIds) newLoaded.add(pid);
        return { loadedChildren: newLoaded };
      });
      return [];
    }

    // Batch fetch in chunks of 200
    const idsArray = Array.from(neededIds);
    const allFetched: SkillFlat[] = [];

    for (let i = 0; i < idsArray.length; i += 200) {
      const chunk = idsArray.slice(i, i + 200);
      const fetched = await skillService.getFlat({ ids: chunk });
      allFetched.push(...fetched);
    }

    set(prev => {
      const newSkills = { ...prev.skills };
      for (const s of allFetched) {
        newSkills[s.id] = s;
      }
      const newLoaded = new Set(prev.loadedChildren);
      for (const pid of parentIds) newLoaded.add(pid);
      return { skills: newSkills, loadedChildren: newLoaded };
    });

    return allFetched;
  },

  expandSkill: async (skillId: number) => {
    const state = get();
    if (state.loadedChildren.has(skillId)) {
      return get().getChildren(skillId);
    }
    await get().loadChildren([skillId]);
    return get().getChildren(skillId);
  },

  loadAllDescendants: async (containerIds: number[]) => {
    const store = get();
    // Collect root skills with children across all containers
    let currentParentIds: number[] = [];
    for (const cid of containerIds) {
      const roots = Object.values(store.skills).filter(
        s => s.container === cid && s.parent === null
      );
      for (const r of roots) {
        if ((r.children_ids.length > 0 || r.child_link_ids.length > 0) && !store.loadedChildren.has(r.id)) {
          currentParentIds.push(r.id);
        }
      }
    }

    // BFS: fetch level by level
    while (currentParentIds.length > 0) {
      const fetched = await get().loadChildren(currentParentIds);

      // Find newly fetched skills that also have children
      const nextParentIds: number[] = [];
      const state = get();
      for (const s of fetched) {
        if ((s.children_ids.length > 0 || s.child_link_ids.length > 0) && !state.loadedChildren.has(s.id)) {
          nextParentIds.push(s.id);
        }
      }
      currentParentIds = nextParentIds;
    }

    // Bump cacheVersion once at the end so UI rebuilds with complete data
    set(prev => ({ cacheVersion: prev.cacheVersion + 1 }));
  },

  getSkill: (id: number) => get().skills[id],

  getChildren: (skillId: number) => {
    const state = get();
    const parent = state.skills[skillId];
    if (!parent) return [];

    const result: Array<SkillFlat & { is_reference: boolean; skill_link_id: number | null }> = [];

    // FK children (not references)
    const fkChildren = parent.children_ids
      .map(id => state.skills[id])
      .filter(Boolean)
      .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));

    for (const child of fkChildren) {
      result.push({ ...child, is_reference: false, skill_link_id: null });
    }

    // SkillLink references
    for (const link of parent.child_link_ids) {
      const child = state.skills[link.skill_id];
      if (child) {
        result.push({ ...child, is_reference: true, skill_link_id: link.link_id });
      }
    }

    return result;
  },

  invalidateContainer: (containerId: number) => {
    set(prev => {
      const newLoaded = new Set(prev.loadedContainers);
      newLoaded.delete(containerId);
      // Remove skills from this container from cache
      const newSkills = { ...prev.skills };
      for (const [id, s] of Object.entries(newSkills)) {
        if (s.container === containerId) {
          delete newSkills[Number(id)];
        }
      }
      // Clear children loaded state for removed skills
      const newLoadedChildren = new Set(prev.loadedChildren);
      for (const id of Object.keys(prev.skills)) {
        const nid = Number(id);
        if (prev.skills[nid]?.container === containerId) {
          newLoadedChildren.delete(nid);
        }
      }
      return { skills: newSkills, loadedContainers: newLoaded, loadedChildren: newLoadedChildren };
    });
  },

  invalidateSkill: (skillId: number) => {
    set(prev => {
      const newSkills = { ...prev.skills };
      delete newSkills[skillId];
      const newLoadedChildren = new Set(prev.loadedChildren);
      newLoadedChildren.delete(skillId);
      // Also invalidate parent so its children_ids refresh
      for (const [, s] of Object.entries(prev.skills)) {
        if (s.children_ids.includes(skillId) || s.child_link_ids.some(l => l.skill_id === skillId)) {
          newLoadedChildren.delete(s.id);
          delete newSkills[s.id];
        }
      }
      return { skills: newSkills, loadedChildren: newLoadedChildren };
    });
  },

  removePattern: (patternId: number) => {
    set(prev => {
      let changed = false;
      const newSkills = { ...prev.skills };
      for (const [id, s] of Object.entries(newSkills)) {
        const filtered = s.reference_patterns_details.filter(p => p.id !== patternId);
        if (filtered.length !== s.reference_patterns_details.length) {
          newSkills[Number(id)] = { ...s, reference_patterns_details: filtered };
          changed = true;
        }
      }
      return changed ? { skills: newSkills, cacheVersion: prev.cacheVersion + 1 } : {};
    });
  },

  clearCache: () => {
    set({ skills: {}, loadedContainers: new Set(), loadedChildren: new Set(), cacheVersion: 0 });
  },
}));

export default useSkillStore;
